import { ResponseError } from "@dreamcam/messenger-sdk";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/DropdownMenu";
import { Image } from "@components/Image";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { EHttpStatusCode } from "@api/config/httpStatusCodes";

import { useDialogStore } from "@storage/global/useDialogStore";
import { useMessengerStore } from "@storage/global/useMessengerStore";

import { DIALOG_KEYS } from "@services/DialogManager";
import { sdkMessenger } from "@services/messenger-sdk";
import { TUser } from "@services/webSocket/types";

import { DELAY_IMMEDIATE } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

interface UserInfoProps {
	user: TUser;
}

const UserInfo = ({ user }: UserInfoProps) => (
	<span className="flex items-center gap-1">
		{user?.isWhale ? <Image width={16} height={16} src={ICON_PATHS.WHALE} alt="whale" /> : null}
		{user?.hasToys ? <Image width={16} height={16} src={ICON_PATHS.HANDY_16} alt="handy" /> : null}
		<Tooltip key="tooltip-host">
			<TooltipTrigger render={<span className="h-6" />}>
				<Typography
					type="span"
					padding="none"
					className="inline-block max-w-full cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
					style={{ color: user?.color }}
				>
					{user?.displayName} {!user.isUnique ? `#${user.postfix}` : null}
				</Typography>
			</TooltipTrigger>
			<TooltipContent>
				<Typography type="span" padding="none">
					{user?.categoryDescription}
				</Typography>
			</TooltipContent>
		</Tooltip>
	</span>
);

interface UserProps {
	user: TUser;
}

export const User = ({ user }: UserProps) => {
	const { openDialog } = useDialogStore();

	const {
		setActiveChat,
		setIsOpenMessenger,
		setIsMessagesBoxFolded,
		isMessagesBoxFolded,
		setMessengerUnsupportedByUser,
	} = useMessengerStore();

	const sdk = sdkMessenger();

	const openPrivateChat = async () => {
		try {
			const createChat = await sdk.chatManager.createPrivateChatAsync(user?.id);
			setActiveChat(null);
			setMessengerUnsupportedByUser({ nickname: "", isDisabled: false, categoryColor: "" });

			setTimeout(() => {
				setIsOpenMessenger(false);
				setActiveChat(createChat.id);
				if (isMessagesBoxFolded) setIsMessagesBoxFolded(false);
			}, DELAY_IMMEDIATE);
		} catch (error) {
			/**
			 * TEMPORARY FIX. CREATED A TASK IN MSNG TO RETURN THE FIELD CODE IN THE ERROR
			 * Its error class name changes during minification
			 */
			const isMessengerUnsupported =
				error instanceof ResponseError
					? error.code === EHttpStatusCode.FORBIDDEN
					: error instanceof Error &&
						(error.message === `${ResponseError.name}: Forbidden` ||
							error.message === `${ResponseError.name}: messenger_unsupported`);

			if (isMessengerUnsupported) {
				setIsOpenMessenger(false);
				setMessengerUnsupportedByUser({
					nickname: user?.displayName,
					categoryColor: user.color,
					isDisabled: true,
				});
				setActiveChat(null);
				if (isMessagesBoxFolded) setIsMessagesBoxFolded(false);
			}
		}
	};

	const menuItems = [
		{
			key: 1,
			label: (
				<Typography size="sm" padding="none" color="error">
					Ban
				</Typography>
			),
			icon: <Image width={24} height={24} src={ICON_PATHS.STOP} alt="stop" />,
			onClick: () => openDialog(DIALOG_KEYS.BLOCK_USER, { userId: user.userId, username: user.displayName }),
		},
		{
			key: 2,
			label: (
				<Typography size="sm" padding="none">
					Mute
				</Typography>
			),
			icon: <Image width={24} height={24} src={ICON_PATHS.MUTE} alt="mute" />,
			onClick: () => openDialog(DIALOG_KEYS.MUTE_USER, { userId: user.userId, username: user.displayName }),
		},
		{
			key: 3,
			label: (
				<Typography size="sm" padding="none">
					Send Message
				</Typography>
			),
			icon: <Image width={24} height={24} src={ICON_PATHS.COVER} alt="cover" />,
			onClick: openPrivateChat,
		},
	];

	return (
		<DropdownMenu key="dropdown-menu-user">
			<DropdownMenuTrigger>
				<UserInfo user={user} />
			</DropdownMenuTrigger>
			<DropdownMenuContent className="min-w-40">
				{menuItems.map((item) => (
					<DropdownMenuItem
						key={item.key}
						onClick={(e) => {
							e.preventDefault();
							item.onClick();
						}}
					>
						{item.icon}
						{item.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
